import { useState, useEffect, useCallback, useRef } from 'react';
import { useCacheContext } from '../context/CacheContext';

const CACHE_PREFIX = 'recapify_cache_';
const CACHE_MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24 hours

function getCacheKey(page) {
  return `${CACHE_PREFIX}${page}`;
}

export function clearAllCache() {
  const keysToRemove = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith(CACHE_PREFIX)) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach(key => localStorage.removeItem(key));
}

export function clearCacheByPrefix(prefix) {
  const fullPrefix = `${CACHE_PREFIX}${prefix}`;
  const keysToRemove = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith(fullPrefix)) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach(key => localStorage.removeItem(key));
}

export default function useCache(page, fetchFn, { enabled = true } = {}) {
  const fetchFnRef = useRef(fetchFn);
  fetchFnRef.current = fetchFn;

  // Read cache synchronously on init so first render already has data
  const initialCache = useRef(null);
  if (initialCache.current === null || initialCache.current.page !== page) {
    const key = getCacheKey(page);
    const raw = localStorage.getItem(key);
    let found = null;
    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        const age = Date.now() - new Date(parsed.cachedAt).getTime();
        if (age < CACHE_MAX_AGE_MS) {
          found = parsed;
        } else {
          localStorage.removeItem(key);
        }
      } catch {
        localStorage.removeItem(key);
      }
    }
    initialCache.current = { page, found };
  }

  const cached = initialCache.current.found;
  const [data, setData] = useState(cached ? cached.data : null);
  const [loading, setLoading] = useState(!cached);
  const [error, setError] = useState('');
  const [cachedAt, setCachedAt] = useState(cached ? new Date(cached.cachedAt) : null);
  const [isCached, setIsCached] = useState(!!cached);
  
  const { registerCache, unregisterCache } = useCacheContext();

  // When page key changes, sync from localStorage or fetch
  useEffect(() => {
    const key = getCacheKey(page);
    const raw = localStorage.getItem(key);
    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        const age = Date.now() - new Date(parsed.cachedAt).getTime();
        if (age < CACHE_MAX_AGE_MS) {
          setData(parsed.data);
          setCachedAt(new Date(parsed.cachedAt));
          setIsCached(true);
          setLoading(false);
          return;
        }
        localStorage.removeItem(key);
      } catch {
        localStorage.removeItem(key);
      }
    }
    if (enabled) {
      refresh();
    } else {
      setLoading(false);
    }
  }, [page, enabled]);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await fetchFnRef.current();
      const now = new Date();
      setData(result);
      setCachedAt(now);
      setIsCached(false);

      const key = getCacheKey(page);
      localStorage.setItem(key, JSON.stringify({ data: result, cachedAt: now.toISOString() }));
    } catch (e) {
      setError(e.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [page]);

  // Update cached data without re-fetching (e.g. after loading more items)
  const update = useCallback((newData) => {
    const now = new Date();
    setData(newData);
    setCachedAt(now);
    setIsCached(true);

    const key = getCacheKey(page);
    localStorage.setItem(key, JSON.stringify({ data: newData, cachedAt: now.toISOString() }));
  }, [page]);

  // Register cache state with context so top nav can display it
  useEffect(() => {
    registerCache(page, { cachedAt, isCached, loading, refresh });
    return () => unregisterCache(page);
  }, [cachedAt, isCached, loading, refresh, registerCache, unregisterCache, page]);

  return { data, loading, error, cachedAt, isCached, refresh, update };
}
